import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { dashboardApi } from '../services/dashboardApi'
import userService from '../services/userService'
import api from '../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/common/LoadingSpinner'

const Profile = () => {
  const { user, updateProfile, logout } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [healthProfile, setHealthProfile] = useState(null)
  const [isEditingHealth, setIsEditingHealth] = useState(false)
  const [stats, setStats] = useState({
    health: { totalAnalyses: 0, recentAnalyses: 0 },
    faces: { totalFaces: 0, totalRecognitions: 0 },
    storage: { used: 0, total: 1000 }
  })
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    preferences: {
      notifications: user?.preferences?.notifications ?? true,
      autoAnalysis: user?.preferences?.autoAnalysis ?? true,
      saveImages: user?.preferences?.saveImages ?? true,
      faceRecognition: user?.preferences?.faceRecognition ?? true
    }
  })
  const [healthFormData, setHealthFormData] = useState({
    age: '',
    gender: '',
    height: '',
    weight: '',
    bloodType: '',
    activityLevel: '',
    medicalConditions: [],
    medications: [],
    allergies: [],
    healthGoals: [],
    dietType: '',
    sleepHours: '',
    smokingStatus: '',
    drinkingHabits: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    primaryDoctor: {
      name: '',
      specialization: '',
      phone: '',
      hospital: ''
    }
  })

  useEffect(() => {
    // Update form data when user changes
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        preferences: {
          notifications: user.preferences?.notifications ?? true,
          autoAnalysis: user.preferences?.autoAnalysis ?? true,
          saveImages: user.preferences?.saveImages ?? true,
          faceRecognition: user.preferences?.faceRecognition ?? true
        }
      })
      

    }
    
    loadStats()
    loadHealthProfile()
  }, [user])



  const loadHealthProfile = async () => {
    try {
      const response = await userService.getHealthProfile()
      if (response.success && response.data) {
        setHealthProfile(response.data)
        
        // Map backend field names to frontend field names
        setHealthFormData({
          age: response.data.age || '',
          gender: response.data.gender || '',
          height: response.data.height || '',
          weight: response.data.weight || '',
          bloodType: response.data.bloodType || '',
          activityLevel: response.data.activityLevel || '',
          medicalConditions: response.data.chronicConditions || [],
          medications: response.data.currentMedications ? response.data.currentMedications.split(',').map(s => s.trim()) : [],
          allergies: response.data.allergies ? response.data.allergies.split(',').map(s => s.trim()) : [],
          healthGoals: response.data.primaryHealthGoals || [],
          dietType: response.data.dietaryPreferences || '',
          sleepHours: response.data.sleepHours || '',
          smokingStatus: response.data.smokingStatus || '',
          drinkingHabits: response.data.alcoholConsumption || '',
          emergencyContact: response.data.emergencyContact || { name: '', relationship: '', phone: '' },
          primaryDoctor: response.data.primaryDoctor || { name: '', specialization: '', phone: '', hospital: '' }
        })
      }
    } catch (error) {
      console.error('Failed to load health profile:', error)
      // Initialize with empty values if no profile exists
      setHealthFormData({
        age: '',
        gender: '',
        height: '',
        weight: '',
        bloodType: '',
        activityLevel: '',
        medicalConditions: [],
        medications: [],
        allergies: [],
        healthGoals: [],
        dietType: '',
        sleepHours: '',
        smokingStatus: '',
        drinkingHabits: '',
        emergencyContact: { name: '', relationship: '', phone: '' },
        primaryDoctor: { name: '', specialization: '', phone: '', hospital: '' }
      })
    }
  }



  const loadStats = async () => {
    setIsLoadingStats(true)
    try {
      const [healthSummary, faceStats, storageData] = await Promise.all([
        dashboardApi.getHealthSummary().catch((error) => {
          console.warn('Health summary failed:', error.message)
          return { data: { totalAnalyses: 0, recentAnalyses: 0 } }
        }),
        dashboardApi.getFaceStats().catch((error) => {
          console.warn('Face stats failed:', error.message)
          if (error.message?.includes('Too many') || error.message?.includes('429')) {
            return { data: { overview: { totalFaces: 'Rate limited', totalRecognitions: 'Rate limited' } } }
          }
          return { data: { overview: { totalFaces: 0, totalRecognitions: 0 } } }
        }),
        dashboardApi.getUserStorage().catch((error) => {
          console.warn('Storage data failed:', error.message)
          return { data: { usage: { total: 0, health: 0, faces: 0, chat: 0 }, limits: { total: 100 }, plan: 'free' } }
        })
      ])
      
      setStats({
        health: healthSummary.data || { totalAnalyses: 0, recentAnalyses: 0 },
        faces: faceStats.data?.overview || { totalFaces: 0, totalRecognitions: 0 },
        storage: {
          used: storageData.data?.usage?.total || 0,
          total: storageData.data?.limits?.total || 100,
          breakdown: {
            health: storageData.data?.usage?.health || 0,
            faces: storageData.data?.usage?.faces || 0,
            chat: storageData.data?.usage?.chat || 0
          },
          plan: storageData.data?.plan || 'free'
        }
      })
    } catch (error) {
      console.error('Failed to load profile stats:', error)
      // Set default values if all API calls fail
      setStats({
        health: { totalAnalyses: 0, recentAnalyses: 0 },
        faces: { totalFaces: 0, totalRecognitions: 0 },
        storage: {
          used: 0,
          total: 100,
          breakdown: { health: 0, faces: 0, chat: 0 },
          plan: 'free'
        }
      })
    } finally {
      setIsLoadingStats(false)
    }
  }

  const handleHealthFormChange = (e) => {
    const { name, value } = e.target
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setHealthFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else if (name === 'medicalConditions' || name === 'medications' || name === 'allergies' || name === 'healthGoals') {
      const values = value.split(',').map(item => item.trim()).filter(item => item)
      setHealthFormData(prev => ({
        ...prev,
        [name]: values
      }))
    } else {
      setHealthFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleHealthSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Map frontend field names to backend field names
      const mappedData = {
        age: healthFormData.age ? Number(healthFormData.age) : undefined,
        height: healthFormData.height ? Number(healthFormData.height) : undefined,
        weight: healthFormData.weight ? Number(healthFormData.weight) : undefined,
        sleepHours: healthFormData.sleepHours ? Number(healthFormData.sleepHours) : undefined,
      };
      
      // Only add enum fields if they have values (not empty strings)
      if (healthFormData.gender && healthFormData.gender !== '') {
        mappedData.gender = healthFormData.gender;
      }
      
      if (healthFormData.bloodType && healthFormData.bloodType !== '') {
        mappedData.bloodType = healthFormData.bloodType;
      }
      
      if (healthFormData.activityLevel && healthFormData.activityLevel !== '') {
        mappedData.activityLevel = healthFormData.activityLevel;
      }
      
      if (healthFormData.smokingStatus && healthFormData.smokingStatus !== '') {
        mappedData.smokingStatus = healthFormData.smokingStatus;
      }
      
      if (healthFormData.drinkingHabits && healthFormData.drinkingHabits !== '') {
        mappedData.alcoholConsumption = healthFormData.drinkingHabits;
      }
      
      if (healthFormData.dietType && healthFormData.dietType !== '') {
        mappedData.dietaryPreferences = healthFormData.dietType;
      }
      
      // Convert arrays to appropriate formats
      if (healthFormData.medicalConditions && healthFormData.medicalConditions.length > 0) {
        mappedData.chronicConditions = Array.isArray(healthFormData.medicalConditions) 
          ? healthFormData.medicalConditions 
          : healthFormData.medicalConditions.split(',').map(s => s.trim()).filter(s => s);
      }
      
      if (healthFormData.medications && healthFormData.medications.length > 0) {
        mappedData.currentMedications = Array.isArray(healthFormData.medications)
          ? healthFormData.medications.join(', ')
          : healthFormData.medications || '';
      }
      
      if (healthFormData.allergies && healthFormData.allergies.length > 0) {
        mappedData.allergies = Array.isArray(healthFormData.allergies)
          ? healthFormData.allergies.join(', ')
          : healthFormData.allergies || '';
      }
      
      if (healthFormData.healthGoals && healthFormData.healthGoals.length > 0) {
        mappedData.primaryHealthGoals = Array.isArray(healthFormData.healthGoals)
          ? healthFormData.healthGoals
          : healthFormData.healthGoals.split(',').map(s => s.trim()).filter(s => s);
      }
      
      // Emergency contact - only add if there's data
      if (healthFormData.emergencyContact?.name || healthFormData.emergencyContact?.phone) {
        mappedData.emergencyContactName = healthFormData.emergencyContact?.name || '';
        mappedData.emergencyContactPhone = healthFormData.emergencyContact?.phone || '';
        mappedData.emergencyContactRelation = healthFormData.emergencyContact?.relationship || '';
      }
      
      // Primary doctor - only add if there's data
      if (healthFormData.primaryDoctor?.name || healthFormData.primaryDoctor?.phone) {
        mappedData.primaryDoctorName = healthFormData.primaryDoctor?.name || '';
        mappedData.primaryDoctorPhone = healthFormData.primaryDoctor?.phone || '';
      }

      console.log('Submitting health profile data:', mappedData);
      const response = await userService.updateHealthProfile(mappedData)
      console.log('Update response:', response);
      
      if (response.success) {
        setHealthProfile(response.data)
        setIsEditingHealth(false)
        toast.success('Health profile updated successfully!')
      } else {
        throw new Error(response.message || 'Failed to update health profile')
      }
    } catch (error) {
      console.error('Health profile update error:', error)
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Show detailed error message
      let errorMessage = 'Failed to update health profile';
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Display error with line breaks if it's a multi-line validation error
      if (errorMessage.includes('\n')) {
        const errors = errorMessage.split('\n');
        errors.forEach(err => {
          if (err.trim()) {
            toast.error(err, { duration: 5000 });
          }
        });
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelHealth = () => {
    if (healthProfile) {
      // Map backend fields to frontend fields when cancelling
      setHealthFormData({
        age: healthProfile.age || '',
        gender: healthProfile.gender || '',
        height: healthProfile.height || '',
        weight: healthProfile.weight || '',
        bloodType: healthProfile.bloodType || '',
        activityLevel: healthProfile.activityLevel || '',
        medicalConditions: healthProfile.chronicConditions || [],
        medications: healthProfile.currentMedications ? healthProfile.currentMedications.split(',').map(s => s.trim()) : [],
        allergies: healthProfile.allergies ? healthProfile.allergies.split(',').map(s => s.trim()) : [],
        healthGoals: healthProfile.primaryHealthGoals || [],
        dietType: healthProfile.dietaryPreferences || '',
        sleepHours: healthProfile.sleepHours || '',
        smokingStatus: healthProfile.smokingStatus || '',
        drinkingHabits: healthProfile.alcoholConsumption || '',
        emergencyContact: healthProfile.emergencyContact || { name: '', relationship: '', phone: '' },
        primaryDoctor: healthProfile.primaryDoctor || { name: '', specialization: '', phone: '', hospital: '' }
      })
    }
    setIsEditingHealth(false)
  }
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: type === 'checkbox' ? checked : value
        }
      }))
      

    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (activeTab === 'profile') {
        // Update basic profile information
        await userService.updateUser({
          name: formData.name,
          email: formData.email
        })
        toast.success('Profile updated successfully!')
      } else if (activeTab === 'settings') {
        // Update user preferences
        await userService.updateUser({
          preferences: formData.preferences
        })
        toast.success('Settings saved successfully!')
        

      }
      
      // Update the auth context with new data
      await updateProfile(formData)
      setIsEditing(false)
      
      // Reload stats to reflect any changes
      loadStats()
    } catch (error) {
      console.error('Profile update error:', error)
      toast.error(error.response?.data?.message || error.message || 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      preferences: {
        notifications: user?.preferences?.notifications ?? true,
        autoAnalysis: user?.preferences?.autoAnalysis ?? true,
        saveImages: user?.preferences?.saveImages ?? true,
        faceRecognition: user?.preferences?.faceRecognition ?? true
      }
    })
    setIsEditing(false)
  }

  const handleDeleteData = async (dataType) => {
    if (!window.confirm(`Are you sure you want to delete all ${dataType} data? This action cannot be undone.`)) {
      return
    }

    setIsLoading(true)
    try {
      if (dataType === 'health') {
        // Get all health records and delete them individually
        const healthRecords = await api.get('/health/records')
        if (healthRecords.data && healthRecords.data.length > 0) {
          // Delete all health records
          await Promise.all(
            healthRecords.data.map(record => api.delete(`/health/${record._id}`))
          )
          toast.success(`Successfully deleted ${healthRecords.data.length} health records`)
        } else {
          toast('No health records found to delete', {
            icon: 'ℹ️',
            duration: 3000
          })
        }
      } else if (dataType === 'faces') {
        // Get all face records and delete them individually
        const faceRecords = await api.get('/faces')
        if (faceRecords.data && faceRecords.data.length > 0) {
          // Delete all face records
          await Promise.all(
            faceRecords.data.map(record => api.delete(`/faces/${record._id}`))
          )
          toast.success(`Successfully deleted ${faceRecords.data.length} face records`)
        } else {
          toast('No face records found to delete', {
            icon: 'ℹ️',
            duration: 3000
          })
        }
      }
      
      // Reload stats to reflect the deletion
      loadStats()
    } catch (error) {
      console.error(`Error deleting ${dataType} data:`, error)
      toast.error(`Failed to delete ${dataType} data: ${error.response?.data?.message || error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportData = async () => {
    setIsLoading(true)
    try {
      // For now, create a client-side export with available data
      const exportData = {
        user: {
          name: user?.name,
          email: user?.email,
          preferences: user?.preferences,
          createdAt: user?.createdAt
        },
        healthProfile: healthProfile,
        stats: stats,
        exportDate: new Date().toISOString()
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `user-data-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      toast.success('Data exported successfully')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export data: ' + (error.response?.data?.message || error.message))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you absolutely sure you want to delete your account? This action is permanent and cannot be undone. All your data will be permanently removed.')) {
      return
    }
    
    if (!window.confirm('This is your final warning. Deleting your account will permanently remove all your health records, face data, and chat history. Type "DELETE" in the next prompt to confirm.')) {
      return
    }
    
    const confirmation = window.prompt('Type "DELETE" to confirm account deletion:')
    if (confirmation !== 'DELETE') {
      toast.error('Account deletion cancelled - confirmation text did not match')
      return
    }

    setIsLoading(true)
    try {
      // For now, show a message that account deletion needs to be handled by support
      toast('Account deletion must be handled by support. Please contact us to proceed.', {
        icon: 'ℹ️',
        duration: 4000
      })
      // await api.delete('/users/delete-account')
      // toast.success('Account deleted successfully')
      // logout() // This will redirect to login
    } catch (error) {
      console.error('Account deletion error:', error)
      toast.error('Failed to delete account: ' + (error.response?.data?.message || error.message))
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout()
    }
  }

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors whitespace-nowrap text-sm sm:text-base ${
        activeTab === id
          ? 'bg-primary-500 text-white shadow-md'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      <span className="text-base sm:text-lg">{icon}</span>
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden text-xs">{label.split(' ')[0]}</span>
    </button>
  )

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Profile Header */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-lg flex-shrink-0">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">{user?.name}</h1>
            <p className="text-gray-600 truncate">{user?.email}</p>
            <p className="text-sm text-gray-500">
              Member since {new Date(user?.createdAt || Date.now()).toLocaleDateString()}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="btn-secondary text-sm sm:text-base"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
            <button
              onClick={handleLogout}
              className="btn bg-red-500 hover:bg-red-600 text-white text-sm sm:text-base"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        <TabButton id="profile" label="Profile" icon="👤" />
        <TabButton id="health" label="Health Profile" icon="🏥" />
        <TabButton id="settings" label="Settings" icon="⚙️" />
        <TabButton id="data" label="Data & Privacy" icon="🔒" />
        <TabButton id="stats" label="Statistics" icon="📊" />
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="input-field"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="input-field"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="small" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'health' && (
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Health Profile</h2>
            <div className="flex space-x-3">
              {!isEditingHealth ? (
                <button
                  onClick={() => setIsEditingHealth(true)}
                  className="btn-secondary text-sm"
                >
                  Edit Health Profile
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={handleCancelHealth}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleHealthSubmit}
                    disabled={isLoading}
                    className="btn-primary text-sm flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="small" />
                        <span className="ml-2">Saving...</span>
                      </>
                    ) : (
                      'Save Health Profile'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-1">Complete Your Health Profile</h4>
                <p className="text-sm text-blue-700">
                  Fill out your health information to get personalized AI insights, health recommendations, and better tracking. 
                  All data is securely stored and you can update it anytime.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleHealthSubmit} className="space-y-8">
            {/* Personal Health Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={healthFormData.age}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Your age"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    name="gender"
                    value={healthFormData.gender}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Blood Type</label>
                  <select
                    name="bloodType"
                    value={healthFormData.bloodType}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select blood type</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={healthFormData.height}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Height in cm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                  <input
                    type="number"
                    name="weight"
                    value={healthFormData.weight}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Weight in kg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activity Level</label>
                  <select
                    name="activityLevel"
                    value={healthFormData.activityLevel}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select activity level</option>
                    <option value="sedentary">Sedentary</option>
                    <option value="lightly-active">Lightly Active</option>
                    <option value="moderately-active">Moderately Active</option>
                    <option value="very-active">Very Active</option>
                    <option value="extremely-active">Extremely Active</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lifestyle Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Lifestyle</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Diet Type</label>
                  <select
                    name="dietType"
                    value={healthFormData.dietType}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select diet type</option>
                    <option value="omnivore">Omnivore</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="vegan">Vegan</option>
                    <option value="pescatarian">Pescatarian</option>
                    <option value="keto">Keto</option>
                    <option value="paleo">Paleo</option>
                    <option value="mediterranean">Mediterranean</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sleep Hours</label>
                  <input
                    type="number"
                    name="sleepHours"
                    value={healthFormData.sleepHours}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Average hours per night"
                    min="0"
                    max="24"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Smoking Status</label>
                  <select
                    name="smokingStatus"
                    value={healthFormData.smokingStatus}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select status</option>
                    <option value="never">Never smoked</option>
                    <option value="former">Former smoker</option>
                    <option value="current">Current smoker</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Drinking Habits</label>
                  <select
                    name="drinkingHabits"
                    value={healthFormData.drinkingHabits}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                  >
                    <option value="">Select frequency</option>
                    <option value="never">Never</option>
                    <option value="rarely">Rarely</option>
                    <option value="occasionally">Occasionally</option>
                    <option value="regularly">Regularly</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Medical Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Conditions <span className="text-xs text-gray-500">(comma-separated)</span>
                  </label>
                  <textarea
                    name="medicalConditions"
                    value={healthFormData.medicalConditions.join(', ')}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    rows="2"
                    placeholder="e.g., Diabetes, Hypertension, Asthma"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Medications <span className="text-xs text-gray-500">(comma-separated)</span>
                  </label>
                  <textarea
                    name="medications"
                    value={healthFormData.medications.join(', ')}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    rows="2"
                    placeholder="e.g., Metformin, Lisinopril, Aspirin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergies <span className="text-xs text-gray-500">(comma-separated)</span>
                  </label>
                  <textarea
                    name="allergies"
                    value={healthFormData.allergies.join(', ')}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    rows="2"
                    placeholder="e.g., Peanuts, Shellfish, Penicillin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Health Goals <span className="text-xs text-gray-500">(comma-separated)</span>
                  </label>
                  <textarea
                    name="healthGoals"
                    value={healthFormData.healthGoals.join(', ')}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    rows="2"
                    placeholder="e.g., Weight loss, Muscle gain, Better sleep, Stress management"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    name="emergencyContact.name"
                    value={healthFormData.emergencyContact.name}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Emergency contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                  <input
                    type="text"
                    name="emergencyContact.relationship"
                    value={healthFormData.emergencyContact.relationship}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="e.g., Spouse, Parent, Sibling"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="emergencyContact.phone"
                    value={healthFormData.emergencyContact.phone}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Emergency contact phone"
                  />
                </div>
              </div>
            </div>

            {/* Primary Doctor */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Primary Doctor</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Doctor Name</label>
                  <input
                    type="text"
                    name="primaryDoctor.name"
                    value={healthFormData.primaryDoctor.name}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Doctor's name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                  <input
                    type="text"
                    name="primaryDoctor.specialization"
                    value={healthFormData.primaryDoctor.specialization}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="e.g., General Practitioner, Cardiologist"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="primaryDoctor.phone"
                    value={healthFormData.primaryDoctor.phone}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Doctor's phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hospital/Clinic</label>
                  <input
                    type="text"
                    name="primaryDoctor.hospital"
                    value={healthFormData.primaryDoctor.hospital}
                    onChange={handleHealthFormChange}
                    disabled={!isEditingHealth}
                    className="input-field"
                    placeholder="Hospital or clinic name"
                  />
                </div>
              </div>
            </div>

            {/* Health Profile Completeness */}
            {healthProfile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-blue-400 text-xl">📊</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Profile Completeness
                    </h3>
                    <p className="mt-1 text-sm text-blue-700">
                      Your health profile helps provide more personalized and accurate health insights.
                    </p>
                    <div className="mt-2">
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${Math.min(100, Object.values(healthFormData).filter(val => 
                            val !== '' && (Array.isArray(val) ? val.length > 0 : true)
                          ).length * 5)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {Object.values(healthFormData).filter(val => 
                          val !== '' && (Array.isArray(val) ? val.length > 0 : true)
                        ).length} of 20 fields completed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Preferences & Settings</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">AI Features</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex-1 mb-3 sm:mb-0">
                    <label className="block text-sm font-medium text-gray-700">Auto-analysis</label>
                    <p className="text-xs text-gray-500 mt-1">Automatically analyze uploaded images</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.autoAnalysis"
                      checked={formData.preferences.autoAnalysis}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {formData.preferences.autoAnalysis ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex-1 mb-3 sm:mb-0">
                    <label className="block text-sm font-medium text-gray-700">Save Images</label>
                    <p className="text-xs text-gray-500 mt-1">Keep uploaded images in your account</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.saveImages"
                      checked={formData.preferences.saveImages}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {formData.preferences.saveImages ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex-1 mb-3 sm:mb-0">
                    <label className="block text-sm font-medium text-gray-700">Face Recognition</label>
                    <p className="text-xs text-gray-500 mt-1">Enable face detection and recognition</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.faceRecognition"
                      checked={formData.preferences.faceRecognition}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {formData.preferences.faceRecognition ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex-1 mb-3 sm:mb-0">
                    <label className="block text-sm font-medium text-gray-700">Notifications</label>
                    <p className="text-xs text-gray-500 mt-1">Receive analysis completion notifications</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="preferences.notifications"
                      checked={formData.preferences.notifications}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {formData.preferences.notifications ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex items-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'data' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Data & Privacy</h2>
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-yellow-400 text-xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Data Management
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Manage your personal data and privacy settings. Deleting data is permanent and cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Health Records</h3>
                  <p className="text-sm text-gray-500">{stats.health.totalAnalyses} records stored</p>
                </div>
                <button
                  onClick={() => handleDeleteData('health')}
                  className="btn bg-red-500 hover:bg-red-600 text-white text-sm w-full sm:w-auto"
                >
                  Delete Health Data
                </button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Face Records</h3>
                  <p className="text-sm text-gray-500">{stats.faces.totalFaces} faces registered</p>
                </div>
                <button
                  onClick={() => handleDeleteData('faces')}
                  className="btn bg-red-500 hover:bg-red-600 text-white text-sm w-full sm:w-auto"
                >
                  Delete Face Data
                </button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Download Data</h3>
                  <p className="text-sm text-gray-500">Export your account data</p>
                </div>
                <button 
                  onClick={handleExportData}
                  disabled={isLoading}
                  className="btn-secondary text-sm w-full sm:w-auto flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="small" />
                      <span className="ml-2">Exporting...</span>
                    </>
                  ) : (
                    'Download Archive'
                  )}
                </button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-red-200 rounded-lg bg-red-50 space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h3 className="font-medium text-red-900">Delete Account</h3>
                  <p className="text-sm text-red-700">Permanently delete your account and all data</p>
                </div>
                <button 
                  onClick={handleDeleteAccount}
                  disabled={isLoading}
                  className="btn bg-red-600 hover:bg-red-700 text-white text-sm w-full sm:w-auto flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="small" />
                      <span className="ml-2">Deleting...</span>
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-6">
          {/* Usage Statistics */}
          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Usage Statistics</h2>
              <button
                onClick={loadStats}
                disabled={isLoadingStats}
                className="btn-secondary text-sm flex items-center"
              >
                {isLoadingStats ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <span className="mr-2">🔄</span>
                    Refresh
                  </>
                )}
              </button>
            </div>
            {isLoadingStats ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="medium" />
                <span className="ml-3 text-gray-600">Loading statistics...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center p-4 sm:p-6 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">{stats.health.totalAnalyses}</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">Health Analyses</div>
                </div>
                <div className="text-center p-4 sm:p-6 bg-green-50 rounded-lg border border-green-100">
                  <div className={`text-2xl sm:text-3xl font-bold ${stats.faces.totalFaces === 'Rate limited' ? 'text-orange-600 text-sm' : 'text-green-600'}`}>
                    {stats.faces.totalFaces === 'Rate limited' ? 'Rate Limited' : stats.faces.totalFaces}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">Faces Registered</div>
                  {stats.faces.totalFaces === 'Rate limited' && (
                    <div className="text-xs text-orange-500 mt-1">Please try again later</div>
                  )}
                </div>
                <div className="text-center p-4 sm:p-6 bg-purple-50 rounded-lg border border-purple-100 sm:col-span-2 lg:col-span-1">
                  <div className={`text-2xl sm:text-3xl font-bold ${stats.faces.totalRecognitions === 'Rate limited' ? 'text-orange-600 text-sm' : 'text-purple-600'}`}>
                    {stats.faces.totalRecognitions === 'Rate limited' ? 'Rate Limited' : stats.faces.totalRecognitions}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">Recognitions</div>
                  {stats.faces.totalRecognitions === 'Rate limited' && (
                    <div className="text-xs text-orange-500 mt-1">Please try again later</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Storage Usage */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Storage Usage</h2>
            {isLoadingStats ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="medium" />
                <span className="ml-3 text-gray-600">Loading storage data...</span>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Used Storage</span>
                    <span className="text-sm text-gray-600">{stats.storage.used}MB / {stats.storage.total}MB</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-300" 
                      style={{ width: `${Math.min(100, (stats.storage.used / stats.storage.total) * 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {Math.max(0, stats.storage.total - stats.storage.used)}MB remaining
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-4">Storage Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">📸 Health Images</span>
                      <span className="text-sm font-medium">{stats.storage.breakdown?.health || 0}MB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">👤 Face Images</span>
                      <span className="text-sm font-medium">{stats.storage.breakdown?.faces || 0}MB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">💬 Chat History</span>
                      <span className="text-sm font-medium">{stats.storage.breakdown?.chat || 0}MB</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Current Plan:</span>
                      <span className="font-medium text-primary-600 capitalize">{stats.storage.plan}</span>
                    </div>
                    {stats.storage.plan === 'free' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Upgrade to Premium for 1GB storage or Pro for 5GB
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile